import React, { useState, useEffect } from 'react';
import { X, Copy, ExternalLink, Share2, Check } from 'lucide-react';

type ShareProfileModalProps = {
  open: boolean;
  onClose: () => void;
  userId: string;
  displayName?: string | null;
};

const ShareProfileModal: React.FC<ShareProfileModalProps> = ({
  open,
  onClose,
  userId,
  displayName,
}) => {
  const [copied, setCopied] = useState(false);

  const profileUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/tasker/${encodeURIComponent(userId)}`
      : '';

  useEffect(() => {
    if (!open) setCopied(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(profileUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  const handleNativeShare = async () => {
    const title = displayName
      ? `${displayName} — GNK Continuum profile`
      : 'GNK Continuum — Tasker profile';
    try {
      if (navigator.share) {
        await navigator.share({ title, url: profileUrl });
      }
    } catch {
      /* user cancelled or unsupported */
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="share-profile-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/65 backdrop-blur-sm"
        aria-label="Close"
        onClick={onClose}
      />
      <div className="relative z-10 w-full max-w-md rounded-t-[2rem] border border-tech-cyan/20 glass-panel-heavy shadow-[0_0_50px_rgba(34,211,238,0.15)] sm:rounded-[2rem] animate-slide-up">
        <div className="flex items-start justify-between gap-3 border-b border-white/[0.06] px-6 py-5">
          <div className="flex items-center gap-4 min-w-0">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-tech-cyan/20 to-blue-500/20 border border-tech-cyan/30 shadow-[0_0_15px_rgba(34,211,238,0.2)]">
              <Share2 className="h-6 w-6 text-tech-cyan" aria-hidden />
            </div>
            <div className="min-w-0">
              <h2 id="share-profile-title" className="font-display text-xl font-bold text-tech-text tracking-tight animated-gradient-text">
                Share your public profile
              </h2>
              <p className="text-xs text-tech-text-muted mt-0.5">
                Anyone with this link can view your tasker profile (no login required).
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-full p-2 text-tech-text-muted hover:bg-white/[0.06] hover:text-tech-text transition-colors border border-transparent hover:border-white/10"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div>
            <label htmlFor="profile-url" className="sr-only">
              Profile URL
            </label>
            <div className="flex rounded-xl border border-tech-cyan/20 bg-tech-sidebar/60 backdrop-blur-md overflow-hidden focus-within:ring-2 focus-within:ring-tech-cyan/50 focus-within:border-tech-cyan/50 transition-all shadow-inner">
              <input
                id="profile-url"
                readOnly
                value={profileUrl}
                className="min-w-0 flex-1 bg-transparent px-4 py-3 text-sm text-tech-text placeholder-tech-text-muted focus:outline-none"
              />
              <button
                type="button"
                onClick={handleCopy}
                className="shrink-0 flex items-center gap-2 border-l border-tech-cyan/20 bg-tech-cyan/5 px-5 py-3 text-sm font-medium text-tech-cyan hover:bg-tech-cyan/15 transition-colors"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    Copy
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            {'share' in navigator && typeof navigator.share === 'function' && (
              <button
                type="button"
                onClick={handleNativeShare}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/[0.1] bg-white/[0.04] px-4 py-2.5 text-sm font-medium text-tech-text hover:bg-white/[0.08] transition-colors order-last sm:order-first"
              >
                <Share2 className="h-4 w-4" />
                Share…
              </button>
            )}
            <a
              href={profileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-tech-blue to-tech-cyan px-5 py-2.5 text-sm font-bold text-black shadow-[0_0_15px_rgba(34,211,238,0.4)] hover:brightness-110 transition-all hover:scale-105"
            >
              <ExternalLink className="h-4 w-4" />
              Open public page
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareProfileModal;
